// src/pages/AuthPages/SignInForm.tsx
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { EyeCloseIcon, EyeIcon } from "../../icons";
import Checkbox from "../form/input/Checkbox";
import Button from "../ui/button/Button";
import { motion } from "framer-motion";
import ForgotPasswordModal from "../auth/ForgotPasswordModal";
import api from "../../services/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import { useAuthSceneStore } from "./authSceneStore";
import "./SignInForm.css";

export default function SignInForm() {
  const navigate = useNavigate();
  // Animated login scene reacts to auth attempts (error → crane holds
  // mid-lift; success → turbine completes and blades spin up)
  const setSceneState = useAuthSceneStore((s) => s.setState);
  const [showPassword, setShowPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    form: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotOpen, setIsForgotOpen] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const LOCK_LIMIT = 3;
  const LOCK_DURATION = 5 * 60 * 1000; // 5 minutes

  const validateForm = () => {
    const newErrors = { email: "", password: "", form: "" };
    let isValid = true;

    if (!email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      newErrors.email = "Invalid email format";
      isValid = false;
    }

    if (!password) {
      newErrors.password = "Password is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // fresh visit to the login page → scene back to its idle erection loop
  useEffect(() => {
    setSceneState("idle");
  }, [setSceneState]);

  useEffect(() => {
    const lockInfo = localStorage.getItem("loginLockInfo");
    if (lockInfo) {
      const parsed = JSON.parse(lockInfo);
      if (parsed.locked && Date.now() < parsed.expiresAt) {
        setIsLocked(true);
      } else {
        localStorage.removeItem("loginLockInfo");
      }
    }
  }, []);

  const { login } = useAuth();

  const fetchRolePermissions = async (roleData: any, token: string) => {
    try {
      console.log("🔍 fetchRolePermissions called with:", {
        roleData,
        roleDataType: typeof roleData,
        isObject: typeof roleData === 'object',
        hasId: roleData?.id || roleData?._id,
        hasPermissions: !!roleData?.permissions
      });

      let permissions: any;

      // Handle role object with permissions (new system) - prioritize this path
      if (typeof roleData === 'object' && roleData.permissions) {
        console.log("📋 Role object has permissions - using directly");
        permissions = roleData.permissions;
        console.log("✅ Using role object permissions:", permissions);
        return permissions;
      }

      // Handle role object with ID but no permissions - fetch from API
      if (typeof roleData === 'object' && (roleData.id || roleData._id)) {
        console.log("📋 Role is object with ID but no permissions - fetching role details by ID");
        const roleId = roleData.id || roleData._id;

        // Fetch role details by ID
        const response = await api.get(`/roles/${roleId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log("📡 Fetched role by ID response:", response.data);
        permissions = response.data.permissions || {};
        console.log("✅ Using fetched role permissions:", permissions);
        return permissions;
      }

      // Handle role ID (string)
      if (typeof roleData === 'string') {
        console.log("🆔 Role is string ID - fetching role details by ID");

        // Fetch role details by ID
        const response = await api.get(`/roles/${roleData}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log("📡 Fetched role by ID response:", response.data);
        permissions = response.data.permissions || {};
        console.log("✅ Using fetched role permissions:", permissions);
        return permissions;
      }

      // Fallback - fetch all roles and find by name (legacy support)
      console.log("🔄 Fallback: Fetching all roles to find by name");
      const response = await api.get("/roles", {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("📡 All roles response:", response.data);

      const role = response.data.find(
        (r: any) => r.name.toLowerCase() === roleData.toLowerCase()
      );

      if (!role) {
        console.error("❌ Role not found in fallback search");
        throw new Error("Role not found");
      }

      console.log("✅ Found role in fallback:", role);
      permissions = role.permissions || {};
      console.log("✅ Using fallback role permissions:", permissions);
      return permissions;

    } catch (error) {
      console.error("❌ Error fetching role permissions:", error);
      throw new Error("Failed to load role permissions");
    }
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLocked) {
      setErrors((prev) => ({
        ...prev,
        form: "Login is locked due to multiple failed attempts. Try again later.",
      }));
      return;
    }

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({ email: "", password: "", form: "" });
    setSceneState("idle");

    try {
      // 1. Login
      console.log("🔐 Starting login process...");
      const authResponse = await api.post("/auth/login", {
        email: email.trim(),
        password,
      });

      console.log("✅ Login API response received:", {
        hasData: !!authResponse?.data,
        responseData: authResponse?.data,
        hasTokens: !!authResponse?.data?.tokens,
        hasUser: !!authResponse?.data?.user,
        hasWrappedData: !!authResponse?.data?.data
      });

      // Handle both wrapped and unwrapped responses
      const responseData = authResponse?.data?.data || authResponse?.data;

      const accessToken = responseData?.tokens?.access?.token;
      const refreshToken = responseData?.tokens?.refresh?.token;
      const userData = responseData?.user;

      console.log("🔍 Extracted data:", {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        hasUserData: !!userData,
        userRole: userData?.role,
        roleType: typeof userData?.role,
        roleHasPermissions: !!(userData?.role?.permissions)
      });

      if (!accessToken || !userData) {
        console.error("❌ Missing required data:", {
          accessToken: !!accessToken,
          userData: !!userData,
          responseStructure: responseData
        });
        throw new Error("Invalid authentication response");
      }

      // 2. Check if user needs role migration (if role is a string, it's old system)
      // if (typeof userData.role === 'string') {
      //   console.log("Migrating user to new role system...");
      //   await migrateUserRole(userData.id, userData.role, accessToken);

      //   // Fetch updated user data
      //   const updatedUserResponse = await api.get(`/users/${userData.id}`, {
      //     headers: { Authorization: `Bearer ${accessToken}` },
      //   });
      //   userData.role = updatedUserResponse.data.role;
      // }

      // 3. Fetch permissions
      let permissions;
      try {
        permissions = await fetchRolePermissions(userData.role, accessToken);
      } catch (permissionError) {
        console.error("Permission loading failed:", permissionError);
        // If permission loading fails but login was successful, use empty permissions as fallback
        console.log("⚠️ Using empty permissions as fallback due to permission loading error");
        permissions = {};
      }

      // 4. Login success - determine role name for login
      let roleName = 'admin'; // default
      if (typeof userData.role === 'string') {
        roleName = userData.role.toLowerCase();
      } else if (typeof userData.role === 'object' && userData.role.name) {
        roleName = userData.role.name.toLowerCase();
      }

      // 5. Store tokens and user data (with multi-department role support).
      //    Per spec §4.13: derive activeDepartment from the user's actual
      //    availableDepartments instead of hardcoding 'OMS'. Prefer
      //    (a) backend-supplied activeDepartment, (b) user's previously saved
      //    department, (c) first available, (d) 'OMS' as final fallback (only
      //    if the user has no departments at all).
      const allDepartments = ['OMS', 'AMS', 'EPC'];
      const userDepartments: string[] =
        userData.roles && Array.isArray(userData.roles) && userData.roles.length > 0
          ? userData.roles.map((r: any) => r.department as string)
          : [];
      const availableDepartments: string[] = userData.isSuperAdmin
        ? allDepartments
        : userDepartments.length > 0
          ? userDepartments
          : ['OMS'];

      // Compute the resolved activeDepartment ourselves so the rolesByDepartment
      // map below uses the same key. AuthContext.login() will also resolve, but
      // mirroring the logic here keeps the two calls in lockstep and lets us
      // build rolesByDepartment with the right default key.
      const savedActive =
        typeof window !== 'undefined'
          ? localStorage.getItem('activeDepartment')
          : null;
      const backendActive: string | undefined = userData.activeDepartment;

      const pickActiveDepartment = (): string => {
        if (backendActive && availableDepartments.includes(backendActive)) {
          return backendActive;
        }
        if (savedActive && availableDepartments.includes(savedActive)) {
          return savedActive;
        }
        if (availableDepartments.length > 0) {
          return availableDepartments[0];
        }
        return 'OMS';
      };
      const activeDepartment: string = pickActiveDepartment();

      const rolesByDepartment: Record<string, any> = userData.roles && Array.isArray(userData.roles) && userData.roles.length > 0
        ? userData.roles.reduce((acc: Record<string, any>, curr: any) => ({ ...acc, [curr.department]: curr }), {})
        : { [activeDepartment]: userData.role };

      login(
        accessToken,
        refreshToken || '',
        roleName,
        permissions,
        userData,
        activeDepartment,
        availableDepartments,
        rolesByDepartment
      );

      // 6. Honor returnTo (set by axios interceptor when a session expired
      // mid-work) before falling back to role-based redirect.
      let targetPath: string | null = null;
      try {
        const stashed = sessionStorage.getItem('returnTo');
        const AUTH_PREFIXES = ['/signin', '/login', '/forgot', '/reset', '/set-password'];
        const normalized = stashed?.toLowerCase();
        const looksLikeSafePath = !!stashed
          && stashed.length < 2048
          && /^\/[^/\\]/.test(stashed)
          && !AUTH_PREFIXES.some(p => normalized!.startsWith(p));
        if (looksLikeSafePath) {
          targetPath = stashed;
          sessionStorage.removeItem('returnTo');
        }
      } catch {
        // sessionStorage unavailable — fall through to role-based redirect
      }

      // No stashed returnTo → go to / so AuthRedirect resolves the correct
      // landing for this user (PMO → /epc/dashboards/pmo, others → first
      // menu item, fallback → /dashboard).
      if (!targetPath) {
        targetPath = "/";
      }

      setSceneState("success");
      navigate(targetPath);

    } catch (error: any) {
      setEmail("");
      setPassword("");

      let message = "Something went wrong. Please try again.";

      if (error?.response) {
        if (error.response?.status === 401) {
          message = "Email or password is incorrect.";
          const newCount = attemptCount + 1;
          setAttemptCount(newCount);

          if (newCount >= LOCK_LIMIT) {
            const lockUntil = Date.now() + LOCK_DURATION;
            setIsLocked(true);
            localStorage.setItem(
              "loginLockInfo",
              JSON.stringify({ locked: true, expiresAt: lockUntil })
            );
            message = "Too many failed attempts. Login locked for 5 minutes.";
          }
        } else {
          message = error.response?.data?.message || message;
        }
      }

      setErrors((prev) => ({ ...prev, form: message }));
      setSceneState("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (errors.email) setErrors({ ...errors, email: "" });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (errors.password) setErrors({ ...errors, password: "" });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="login-glass-card relative w-full max-w-md rounded-2xl overflow-hidden"
    >
      <div className="relative z-10 w-full px-8 py-8">
        <div>
          <div className="flex flex-col items-center mb-6">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
              className="text-center mb-4"
            >
              <p className="text-sm text-[#5A6B65]">
                Sign in to your Renewable Operations workspace
              </p>
            </motion.div>
          </div>

          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="space-y-5"
          >
            {/* Email Field */}
            <div className="w-full">
              <label className="block text-xs font-semibold text-[#2A1F17] mb-1.5 tracking-wide uppercase ml-1">
                Email
              </label>
              <div
                className={`group relative flex items-center h-14 px-4 rounded-xl transition-all duration-300
                  ${errors.email
                    ? "bg-red-50/70 backdrop-blur border border-error-500 ring-2 ring-error-500/20"
                    : "login-glass-input"
                  }`}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 30 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="shrink-0"
                >
                  <path
                    d="M4.95289 9.71255C4.7179 10.6695 4.54627 11.9052 4.30751 13.6244C3.76229 17.5499 3.48969 19.5126 4.08909 21.0371C4.61531 22.3754 5.58692 23.4913 6.8401 24.1966C8.26756 25 10.2492 25 14.2124 25H16.2426C20.2059 25 22.1875 25 23.615 24.1966C24.8681 23.4913 25.8397 22.3754 26.366 21.0371C26.9654 19.5126 26.6927 17.5499 26.1476 13.6244C25.8882 11.7573 25.7081 10.4604 25.44 9.47159L24.527 10.3846C22.4866 12.425 20.8877 14.024 19.4724 15.1039C18.0237 16.2091 16.6579 16.8585 15.076 16.8585C13.4941 16.8585 12.1283 16.2091 10.6796 15.1039C9.26427 14.024 7.66531 12.425 5.62497 10.3846L4.95289 9.71255Z"
                    fill="#0B3B2E"
                  />
                  <path
                    d="M5.71875 7.82637L6.90045 9.00807C9.00253 11.1101 10.5138 12.6186 11.8173 13.6131C13.0993 14.5912 14.0694 14.9835 15.0764 14.9835C16.0834 14.9835 17.0535 14.5912 18.3354 13.6131C19.639 12.6186 21.1503 11.1101 23.2523 9.00807L24.6144 7.64595C23.9795 6.7463 23.1169 6.02886 22.1128 5.56854C20.8725 5 19.3294 5 16.243 5H14.2127C11.1265 5 9.58333 5 8.34313 5.56854C7.27274 6.05924 6.3632 6.84209 5.71875 7.82637Z"
                    fill="#0B3B2E"
                  />
                </svg>

                <input
                  type="email"
                  placeholder="you@sorigin.com"
                  value={email}
                  onChange={handleEmailChange}
                  disabled={isLoading}
                  className="flex-1 bg-transparent border-none outline-none text-[#1A3328] placeholder-[#9CA39B] text-sm h-full ml-3"
                />
              </div>
              {errors.email && (
                <p className="mt-1.5 text-sm text-error-500 ml-1">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="w-full">
              <label className="block text-xs font-semibold text-[#2A1F17] mb-1.5 tracking-wide uppercase ml-1">
                Password
              </label>
              <div
                className={`group relative flex items-center h-14 px-4 rounded-xl transition-all duration-300
                  ${errors.password
                    ? "bg-red-50/70 backdrop-blur border border-error-500 ring-2 ring-error-500/20"
                    : "login-glass-input"
                  }`}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 30 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="shrink-0"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.8125 8.75C7.8125 4.78045 11.0304 1.5625 15 1.5625C18.9695 1.5625 22.1875 4.78045 22.1875 8.75V10.1422C22.1875 10.2257 22.1766 10.3066 22.1561 10.3837C23.9512 11.0433 25.3504 12.4915 25.9441 14.3186C26.25 15.2601 26.25 16.4234 26.25 18.75C26.25 21.0766 26.25 22.2399 25.9441 23.1814C25.3259 25.0841 23.8341 26.5759 21.9314 27.1941C20.9899 27.5 19.8266 27.5 17.5 27.5H12.5C10.1734 27.5 9.0101 27.5 8.06865 27.1941C6.16591 26.5759 4.67414 25.0841 4.0559 23.1814C3.75 22.2399 3.75 21.0766 3.75 18.75C3.75 16.4234 3.75 15.2601 4.0559 14.3186C4.64957 12.4915 6.04879 11.0433 7.8439 10.3837C7.82341 10.3066 7.8125 10.2257 7.8125 10.1422V8.75ZM9.6875 10.0427C10.3997 10 11.2934 10 12.5 10H17.5C18.7066 10 19.6004 10 20.3125 10.0427V8.75C20.3125 5.81599 17.934 3.4375 15 3.4375C12.066 3.4375 9.6875 5.81599 9.6875 8.75V10.0427ZM15 15.3125C15.5177 15.3125 15.9375 15.7323 15.9375 16.25V21.25C15.9375 21.7678 15.5177 22.1875 15 22.1875C14.4823 22.1875 14.0625 21.7678 14.0625 21.25V16.25C14.0625 15.7323 14.4823 15.3125 15 15.3125Z"
                    fill="#0B3B2E"
                  />
                </svg>

                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={handlePasswordChange}
                  disabled={isLoading}
                  className="flex-1 bg-transparent border-none outline-none text-[#1A3328] placeholder-[#9CA39B] text-sm h-full ml-3"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-[#5A6B65] hover:text-[#076333] transition cursor-pointer"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeIcon className="size-5" />
                  ) : (
                    <EyeCloseIcon className="size-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-sm text-error-500 ml-1">{errors.password}</p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={isChecked}
                  onChange={() => setIsChecked(!isChecked)}
                  disabled={isLoading}
                />
                <span className="text-sm text-[#5A6B65] font-medium">
                  Remember me
                </span>
              </div>
              <Link
                to="#"
                onClick={() => setIsForgotOpen(true)}
                className="text-sm font-semibold text-[#076333] hover:text-[#A6CE39] transition"
              >
                Forgot password?
              </Link>
            </div>
            {errors.form && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
              >
                <svg
                  className="mt-0.5 shrink-0 text-red-500"
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01M12 19a7 7 0 110-14 7 7 0 010 14z"
                  />
                </svg>
                <div className="flex-1">
                  <span className="font-medium">{errors.form}</span>
                  {!isLocked && attemptCount > 0 && (
                    <span className="text-amber-700 pl-2">
                      (Attempt{" "}
                      <span className="font-semibold">{attemptCount}</span> of{" "}
                      <span className="font-semibold">{LOCK_LIMIT}</span>)
                    </span>
                  )}
                </div>
              </motion.div>
            )}

            {/* Submit Button — Sorigin brand gradient */}
            <motion.button
              type="submit"
              disabled={isLoading}
              whileHover={!isLoading ? { scale: 1.015, y: -1 } : undefined}
              whileTap={!isLoading ? { scale: 0.985 } : undefined}
              className="login-glass-btn relative w-full h-14 rounded-xl overflow-hidden text-white font-semibold tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="relative flex items-center justify-center gap-2">
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign in
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </span>
            </motion.button>

            {/* Footer */}
            <div className="text-center pt-2">
              <p className="text-xs text-[#5A6B65]">
                © {new Date().getFullYear()} Sorigin Group · All systems operational
              </p>
            </div>
          </motion.form>
        </div>
      </div>
      <ForgotPasswordModal
        isOpen={isForgotOpen}
        onClose={() => setIsForgotOpen(false)}
      />
    </motion.div>
  );
}